import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LogOut, User, Store, MapPin, Phone, ChevronLeft, Sparkles, Globe, Bell, BellOff, Mic, Ticket, FileText, Landmark, BellRing, ChevronRight, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ReferralCard from "@/components/ReferralCard";
import { useT } from "@/lib/i18n";

const LANG_LABELS = {
  english: "🇬🇧 English",
  pidgin: "🇳🇬 Pidgin",
  yoruba: "🟢 Yorùbá",
  hausa: "🟢 Hausa",
  igbo: "🟢 Igbo",
};

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const t = useT(user?.preferred_language);
  const [showLang, setShowLang] = useState(false);
  // Iter49: account-deletion state.
  const [showDelete, setShowDelete] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const updateLang = async (lang) => {
    try {
      await api.post("/auth/language", { preferred_language: lang });
      await refresh();
      toast.success("Language updated");
      setShowLang(false);
    } catch { toast.error("Failed"); }
  };

  const handleDeleteAccount = async () => {
    if (deleteInput.trim().toUpperCase() !== "DELETE") {
      toast.error('Type "DELETE" to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { confirmation: "DELETE" });
      toast.success("Your account and data have been permanently deleted");
      // Clear client-side auth state and bounce to landing.
      logout();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete account. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="px-4 py-5 space-y-5 pb-6">
      <div className="flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-accent flex items-center justify-center" data-testid="profile-back">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h1 className="font-heading text-xl font-bold">{t("profileTitle")}</h1>
      </div>

      <div className="bg-white border border-border rounded-2xl p-5 text-center">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-kudi-green to-kudi-terracotta text-white flex items-center justify-center font-heading font-bold text-3xl mx-auto mb-3">
          {user?.owner_name?.[0]?.toUpperCase() || "K"}
        </div>
        <div className="font-heading text-xl font-bold">{user?.owner_name}</div>
        <div className="text-sm text-kudi-ink/60 mt-0.5">{user?.business_name}</div>
      </div>

      <ReferralCard />

      {/* More tools — nested routes moved out of the primary 5-tab nav */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="profile-more-menu">
        <div className="px-4 pt-4 pb-2 text-[11px] uppercase tracking-wider text-kudi-ink/45 font-semibold">Business tools</div>
        <div className="divide-y divide-border/40">
          <MenuLink icon={FileText} label="Documents" hint="Quotations, invoices & receipts" onClick={() => nav("/app/documents")} testid="menu-documents" badge="New" />
          <MenuLink icon={Landmark} label="Loans" hint="Working-capital advances & repayments" onClick={() => nav("/app/loans")} testid="menu-loans" badge="New" />
          <MenuLink icon={BellRing} label="Reminders" hint="Debtor nudges & schedules" onClick={() => nav("/app/reminders")} testid="menu-reminders" badge="New" />
        </div>
      </div>

      {/* Notifications toggle — /api/me/notifications */}
      <NotificationsToggle user={user} onChange={refresh} />

      {/* Usage / quota tile — /api/usage/quota */}
      <QuotaTile />

      {/* Apply someone else's referral code — /api/referral/apply */}
      {!user?.referred_by && <ReferralApplyCard onApplied={refresh} />}

      <div className="bg-white border border-border rounded-2xl divide-y divide-border/40">
        <Row icon={Phone} label={t("phone")} value={user?.phone} />
        <Row icon={Store} label={t("businessType")} value={user?.business_type} />
        <Row icon={MapPin} label={t("location")} value={user?.location} />
        <Row icon={User} label={t("memberSince")} value={new Date(user?.created_at).toLocaleDateString("en-NG", { month: "long", year: "numeric" })} />
        <button onClick={() => setShowLang(true)} className="w-full text-left p-4 flex items-center gap-3 hover:bg-accent/40 transition" data-testid="change-language-btn">
          <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center shrink-0">
            <Globe className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-kudi-ink/55">{t("language")}</div>
            <div className="text-sm font-medium truncate">{LANG_LABELS[user?.preferred_language] || "🇬🇧 English"}</div>
          </div>
          <div className="text-xs text-kudi-green font-semibold">{t("change")}</div>
        </button>
      </div>

      {showLang && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setShowLang(false)}>
          <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-3xl p-5" onClick={(e) => e.stopPropagation()} data-testid="lang-modal">
            <h3 className="font-heading text-lg font-bold mb-3">{t("chooseLanguage")}</h3>
            <div className="space-y-2">
              {Object.entries(LANG_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => updateLang(k)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition ${
                    user?.preferred_language === k
                      ? "bg-kudi-green text-white border-kudi-green"
                      : "bg-white border-border hover:border-kudi-green/40"
                  }`}
                  data-testid={`lang-opt-${k}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => nav("/app/pricing")}
        className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-white rounded-2xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition"
        data-testid="upgrade-btn"
      >
        <Sparkles className="w-4 h-4" /> {user?.is_premium ? t("manageSubscription") : t("upgradeToPro")}
      </button>

      <button
        onClick={handleLogout}
        className="w-full bg-white border border-rose-200 text-rose-600 rounded-2xl py-3.5 font-semibold flex items-center justify-center gap-2 hover:bg-rose-50 transition"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4" /> {t("signOut")}
      </button>

      {/* Iter49: Delete Account — clearly separated, red danger tone. */}
      <button
        onClick={() => setShowDelete(true)}
        className="w-full bg-white border border-rose-300 text-rose-700 rounded-2xl py-3 font-semibold flex items-center justify-center gap-2 hover:bg-rose-50 transition"
        data-testid="delete-account-btn"
      >
        <Trash2 className="w-4 h-4" /> Delete my account
      </button>

      <div className="text-center text-xs text-kudi-ink/40 mt-8">
        Kobolog AI · v0.2<br />
        Made with ❤️ in Lagos
      </div>

      {/* Delete-account confirm dialog */}
      {showDelete && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => !deleting && setShowDelete(false)}
          data-testid="delete-account-backdrop"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-account-modal"
          >
            <div className="p-5 flex items-start gap-3 border-b border-border">
              <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-lg">Delete your account?</div>
                <div className="text-sm text-kudi-ink/65 mt-1">
                  This permanently removes your transactions, inventory, debtors, reminders, chat history and profile.
                  It cannot be undone.
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs uppercase tracking-wider text-kudi-ink/60 font-semibold block">
                Type <span className="font-mono text-rose-700">DELETE</span> to confirm
              </label>
              <input
                autoFocus
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="DELETE"
                className="w-full border border-border rounded-xl px-3 py-2 text-sm font-mono uppercase focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                data-testid="delete-account-input"
              />
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => { setShowDelete(false); setDeleteInput(""); }}
                  disabled={deleting}
                  className="flex-1 py-2.5 rounded-lg border border-border font-semibold text-sm hover:bg-accent/40 transition disabled:opacity-50"
                  data-testid="delete-account-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteInput.trim().toUpperCase() !== "DELETE"}
                  className="flex-1 py-2.5 rounded-lg bg-rose-600 text-white font-semibold text-sm hover:bg-rose-700 transition inline-flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-account-confirm"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {deleting ? "Deleting..." : "Delete forever"}
                </button>
              </div>
              <div className="text-[10px] text-kudi-ink/45 text-center pt-1">
                Prefer to request via a form? Visit <a href="/delete-account" className="underline">kobolog.com.ng/delete-account</a>.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-kudi-ink/55">{label}</div>
        <div className="text-sm font-medium truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

function MenuLink({ icon: Icon, label, hint, onClick, testid, badge }) {
  return (
    <button
      onClick={onClick}
      className="w-full p-4 flex items-center gap-3 hover:bg-accent/40 transition text-left"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold flex items-center gap-2">
          {label}
          {badge && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-kudi-green/10 text-kudi-green px-1.5 py-0.5 rounded-full" data-testid={`${testid}-badge`}>
              {badge}
            </span>
          )}
        </div>
        <div className="text-[11px] text-kudi-ink/55 truncate">{hint}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-kudi-ink/40" />
    </button>
  );
}

function NotificationsToggle({ user, onChange }) {
  const [optIn, setOptIn] = useState(user?.notifications_opt_in !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setOptIn(user?.notifications_opt_in !== false); }, [user?.notifications_opt_in]);

  const flip = async () => {
    const next = !optIn;
    setSaving(true);
    setOptIn(next); // optimistic
    try {
      await api.patch("/me/notifications", { notifications_opt_in: next });
      toast.success(next ? "Notifications ON — daily brief, low stock and debtor reminders will keep flowing." : "Notifications muted.");
      onChange && onChange();
    } catch (err) {
      setOptIn(!next); // rollback
      toast.error(err?.response?.data?.detail || "Could not update preferences");
    } finally { setSaving(false); }
  };

  return (
    <button
      onClick={flip}
      disabled={saving}
      className="w-full bg-white border border-border rounded-2xl py-3.5 text-sm font-medium flex items-center justify-between px-4 hover:border-kudi-green/40 transition disabled:opacity-70"
      data-testid="notifications-toggle"
    >
      <span className="flex items-center gap-2">
        {optIn ? <Bell className="w-4 h-4 text-kudi-green" /> : <BellOff className="w-4 h-4 text-kudi-ink/40" />}
        <span className="text-left">
          <span className="block font-semibold">WhatsApp notifications</span>
          <span className="block text-[11px] text-kudi-ink/55">
            {optIn ? "Daily brief, low stock alerts & debtor reminders" : "All automated pings paused"}
          </span>
        </span>
      </span>
      <span className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${optIn ? "bg-kudi-green justify-end" : "bg-kudi-ink/15 justify-start"}`}>
        <span className="w-5 h-5 rounded-full bg-white shadow" />
      </span>
    </button>
  );
}

function QuotaTile() {
  const [q, setQ] = useState(null);
  useEffect(() => { api.get("/usage/quota").then((r) => setQ(r.data)).catch(() => {}); }, []);
  if (!q) return null;
  if (q.is_premium) {
    return (
      <div className="bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-300 rounded-2xl p-4 flex items-center gap-3" data-testid="quota-tile">
        <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
        <div>
          <div className="text-xs font-semibold text-amber-900">Pro plan · Unlimited</div>
          <div className="text-[11px] text-amber-900/70 mt-0.5">Send unlimited voice notes and receipt photos. No monthly cap.</div>
        </div>
      </div>
    );
  }
  const pct = q.limit ? Math.min(100, Math.round((q.used / q.limit) * 100)) : 0;
  const barColor = pct < 60 ? "bg-emerald-500" : pct < 85 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="bg-white border border-border rounded-2xl p-4" data-testid="quota-tile">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center"><Mic className="w-4 h-4" /></div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Voice & photo quota</div>
          <div className="text-[11px] text-kudi-ink/55">Free plan · resets 1st of each month</div>
        </div>
        <div className="text-sm font-bold tabular-nums">{q.used}<span className="text-kudi-ink/50 font-normal">/{q.limit}</span></div>
      </div>
      <div className="h-2 rounded-full bg-kudi-cream overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {q.remaining <= 5 && !q.is_premium && (
        <div className="mt-2 text-[11px] text-rose-600 font-medium">Running low — upgrade for unlimited AI parsing.</div>
      )}
    </div>
  );
}

function ReferralApplyCard({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!code.trim()) return toast.error("Enter a code first");
    setSaving(true);
    try {
      const r = await api.post("/referral/apply", { code: code.trim().toUpperCase() });
      toast.success(`Applied — you're linked to ${r.data.inviter || "your friend"} 🎉`);
      setOpen(false);
      onApplied && onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply that code");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-4" data-testid="apply-referral-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Ticket className="w-4 h-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Got a referral code?</div>
          <div className="text-[11px] text-kudi-ink/55 mt-0.5">Enter your friend&apos;s code and both of you get a boost.</div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-indigo-700" data-testid="apply-referral-open">Enter code</button>
        )}
      </div>
      {open && (
        <div className="mt-3 flex gap-2">
          <input
            type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="KOBO-XXXX"
            maxLength={20}
            className="flex-1 border border-border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:border-indigo-500 focus:outline-none"
            data-testid="apply-referral-input"
            autoFocus
          />
          <button onClick={apply} disabled={saving || !code.trim()} className="text-xs bg-indigo-600 text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 hover:bg-indigo-700" data-testid="apply-referral-submit">
            {saving ? "…" : "Apply"}
          </button>
          <button onClick={() => { setOpen(false); setCode(""); }} className="text-xs text-kudi-ink/50 px-2">Cancel</button>
        </div>
      )}
    </div>
  );
}
